// src/pages/SettingsPage.js - FIXED API CALLS
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useNotifications } from "../contexts/NotificationContext";
import { apiService } from "../services/apiService"; // ADDED: Use centralized API service

export default function SettingsPage() {
  const navigate = useNavigate();
  const [profileName, setProfileName] = useState("");
  const [user, setUser] = useState(null);
  const [netlifyTokens, setNetlifyTokens] = useState([]);
  const [awsCredentials, setAwsCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testingCredentials, setTestingCredentials] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'netlify' or 'aws'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [stats, setStats] = useState({ netlifyUsage: 0, awsUsage: 0, totalSites: 0 });
  
  // Form states
  const [netlifyForm, setNetlifyForm] = useState({ token_name: "", token: "" });
  const [awsForm, setAwsForm] = useState({ cred_name: "", access_key_id: "", secret_access_key: "" });
  
  const { success, error, loading: showLoading, updateNotification } = useNotifications();

  useEffect(() => {
    loadUserAndData();
  }, []);

  const loadUserAndData = async () => {
    const { data } = await supabase.auth.getUser();
    const user = data?.user;
    setUser(user || null);
    setProfileName(
      user?.user_metadata?.name ||
      user?.email?.split("@")[0] ||
      "User"
    );
    
    if (!user) {
      setNetlifyTokens([]);
      setAwsCredentials([]);
      return;
    }

    // Load credentials and sites data
    try {
      const [netlifyData, awsData, sitesData] = await Promise.allSettled([
        supabase.from("netlify_tokens").select("*").eq("user_id", user.id),
        supabase.from("aws_credentials").select("*").eq("user_id", user.id),
        apiService.getSites(user.id) // FIXED: Use apiService instead of direct supabase call
      ]);

      // Handle netlify data
      if (netlifyData.status === 'fulfilled') {
        setNetlifyTokens(netlifyData.value.data || []);
      } else {
        console.warn('Failed to load Netlify tokens:', netlifyData.reason);
        setNetlifyTokens([]);
      }

      // Handle AWS data
      if (awsData.status === 'fulfilled') {
        setAwsCredentials(awsData.value.data || []);
      } else {
        console.warn('Failed to load AWS credentials:', awsData.reason);
        setAwsCredentials([]);
      }

      // Handle sites data for usage stats
      if (sitesData.status === 'fulfilled') {
        const sites = sitesData.value || [];
        setStats({
          netlifyUsage: Array.isArray(sites) ? sites.filter(s => s.platform === 'netlify').length : 0,
          awsUsage: Array.isArray(sites) ? sites.filter(s => s.platform === 'aws').length : 0,
          totalSites: Array.isArray(sites) ? sites.length : 0
        });
      } else {
        console.warn('Failed to load sites data:', sitesData.reason);
        setStats({ netlifyUsage: 0, awsUsage: 0, totalSites: 0 });
      }

    } catch (err) {
      console.error('Error loading user data:', err);
      error('Failed to load some data. Please refresh the page.');
    }
  };

  const openAddModal = (type) => {
    setModalType(type);
    setShowAddModal(true);
    if (type === 'netlify') {
      setNetlifyForm({ token_name: "", token: "" });
    } else {
      setAwsForm({ cred_name: "", access_key_id: "", secret_access_key: "" });
    }
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setModalType('');
  };

  // FIXED: Use apiService instead of hard-coded localhost
  const testConnection = async (type, id, credentials) => {
    setTestingCredentials(prev => ({ ...prev, [id]: true }));
    const loadingId = showLoading(`Testing ${type} connection...`);
    
    try {
      // Use apiService for consistent API calls
      const result = await apiService.testCredentials(type.toLowerCase(), credentials);
      
      if (result.success) {
        updateNotification(loadingId, {
          type: 'success',
          message: `${type} connection successful!`,
          duration: 3000
        });
      } else {
        throw new Error(result.error || 'Connection failed');
      }
    } catch (err) {
      updateNotification(loadingId, {
        type: 'error',
        message: `${type} connection failed: ${err.message}`,
        duration: 5000
      });
    } finally {
      setTestingCredentials(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleAddCredential = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (modalType === 'netlify') {
        const { error } = await supabase.from("netlify_tokens").insert([
          {
            user_id: user.id,
            token_name: netlifyForm.token_name,
            token: netlifyForm.token,
          },
        ]);
        if (error) throw error;
        success("Netlify token added successfully!");
      } else {
        const { error } = await supabase.from("aws_credentials").insert([
          {
            user_id: user.id,
            cred_name: awsForm.cred_name,
            access_key_id: awsForm.access_key_id,
            secret_access_key: awsForm.secret_access_key,
          },
        ]);
        if (error) throw error;
        success("AWS credentials added successfully!");
      }
      
      closeAddModal();
      loadUserAndData();
    } catch (err) {
      error("Error adding credentials: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRequest = async (type, id, name) => {
    // Check if credential is being used
    try {
      const sites = await apiService.getSites(user.id);
      const usingSites = Array.isArray(sites) ? sites.filter(site => site.account_id === id) : [];

      if (usingSites.length > 0) {
        const siteList = usingSites.map(site => 
          `${site.service}-${site.city}-${site.state_code}`
        ).join(", ");
        
        error(`Cannot delete "${name}". It's connected to ${usingSites.length} site(s): ${siteList}. Please change the deployment account for these sites first.`);
        return;
      }
    } catch (err) {
      console.warn('Could not check site usage:', err);
      // Continue with deletion - the error might be non-critical
    }

    setDeleteTarget({ type, id, name });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    
    setLoading(true);
    
    try {
      const tableName = deleteTarget.type === 'netlify' ? 'netlify_tokens' : 'aws_credentials';
      const { error } = await supabase.from(tableName).delete().eq('id', deleteTarget.id);

      if (error) throw error;
      
      success(`${deleteTarget.name} deleted successfully!`);
      loadUserAndData();
    } catch (err) {
      error(`Error deleting credential: ${err.message}`);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const getStatusColor = (isConnected) => {
    return isConnected ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600';
  };

  const getStatusIcon = (isConnected) => {
    if (isConnected) {
      return (
        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="mt-2 text-gray-600">Manage your deployment credentials and account settings</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <div className="flex items-center space-x-3 text-sm text-gray-500">
            <span>Welcome back,</span>
            <span className="font-semibold text-indigo-700">{profileName}</span>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-50 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Sites</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalSites}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-gray-50 rounded-lg">
              <span className="text-gray-900 font-bold text-sm">N</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Netlify Sites</p>
              <p className="text-2xl font-bold text-gray-900">{stats.netlifyUsage}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-50 rounded-lg">
              <span className="text-orange-600 font-bold text-xs">AWS</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">AWS Sites</p>
              <p className="text-2xl font-bold text-gray-900">{stats.awsUsage}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Netlify Credentials */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">N</span>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Netlify Tokens</h2>
                <p className="text-sm text-gray-500">Manage your Netlify deployment tokens</p>
              </div>
            </div>
            <button
              onClick={() => openAddModal('netlify')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Add Token</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          {netlifyTokens.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gray-900 font-bold text-lg">N</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Netlify tokens yet</h3>
              <p className="text-gray-500 mb-4">Add your first Netlify token to start deploying sites</p>
              <button
                onClick={() => openAddModal('netlify')}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Add Your First Token
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {netlifyTokens.map(token => (
                <div key={token.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{token.token_name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(true)}`}>
                          Active
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {token.token.slice(0, 8)}...{token.token.slice(-4)}
                      </p>
                    </div>
                    {getStatusIcon(true)}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Added {new Date(token.created_at).toLocaleDateString() || 'Recently'}
                    </div>
                    
                    <button
                      onClick={() => handleDeleteRequest('netlify', token.id, token.token_name)}
                      className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors"
                      title="Delete Token"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* AWS Credentials */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">AWS</span>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">AWS S3 Credentials</h2>
                <p className="text-sm text-gray-500">Manage your AWS S3 deployment credentials</p>
              </div>
            </div>
            <button
              onClick={() => openAddModal('aws')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Add Credentials</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          {awsCredentials.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-orange-600 font-bold text-sm">AWS</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No AWS credentials yet</h3>
              <p className="text-gray-500 mb-4">Add your AWS credentials to deploy sites to S3</p>
              <button
                onClick={() => openAddModal('aws')}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Add Your First Credentials
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {awsCredentials.map(cred => (
                <div key={cred.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{cred.cred_name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(true)}`}>
                          Active
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {cred.access_key_id.slice(0, 8)}...{cred.access_key_id.slice(-4)}
                      </p>
                    </div>
                    {getStatusIcon(true)}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Added {new Date(cred.created_at).toLocaleDateString() || 'Recently'}
                    </div>
                    
                    <button
                      onClick={() => handleDeleteRequest('aws', cred.id, cred.cred_name)}
                      className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors"
                      title="Delete Credentials"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Credential Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Add {modalType === 'netlify' ? 'Netlify Token' : 'AWS Credentials'}
                </h3>
                <button
                  onClick={closeAddModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <form onSubmit={handleAddCredential} className="p-6 space-y-4">
              {modalType === 'netlify' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Token Name
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Main Account, Production"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      value={netlifyForm.token_name}
                      onChange={e => setNetlifyForm(f => ({ ...f, token_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Netlify Token
                    </label>
                    <input
                      type="password"
                      required
                      placeholder="Your Netlify access token"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      value={netlifyForm.token}
                      onChange={e => setNetlifyForm(f => ({ ...f, token: e.target.value }))}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Credentials Name
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Production AWS, Dev Environment"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      value={awsForm.cred_name}
                      onChange={e => setAwsForm(f => ({ ...f, cred_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Access Key ID
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="AKIA..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      value={awsForm.access_key_id}
                      onChange={e => setAwsForm(f => ({ ...f, access_key_id: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Secret Access Key
                    </label>
                    <input
                      type="password"
                      required
                      placeholder="Your AWS secret access key"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      value={awsForm.secret_access_key}
                      onChange={e => setAwsForm(f => ({ ...f, secret_access_key: e.target.value }))}
                    />
                  </div>
                </>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeAddModal}
                  disabled={loading}
                  className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Adding...</span>
                    </>
                  ) : (
                    <span>Add Credentials</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
              
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete "<span className="font-semibold">{deleteTarget?.name}</span>"? 
                This will permanently remove the credentials from your account.
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Back to Dashboard */}
      <div className="flex justify-center">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>Back to Dashboard</span>
        </button>
      </div>
    </div>
  );
}